import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import { Avatar, Button, LogoIcon } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { useDensity } from "@/lib/prefs/density";
import { cn } from "@/lib/utils";
import { NotificationsBell } from "./NotificationsBell";
import { initials, LangToggle, SidebarNav, UserMenu, type NavGroupDef } from "./parts";

/**
 * Club Member shell (architecture §3.3 / §4.3): single-club tenant chrome — sidebar with
 * the club's own nav, a topbar with search, the EN/ES toggle, notifications and the account
 * menu. Ported from the prototype's shell.jsx (design/prototype/project/app). Mounted for
 * CLUB_USER accounts only; the API enforces the same boundary regardless.
 *
 * Nav labels and chrome are i18n keys (TW-3434) resolved live by the language toggle.
 */
const NAV: NavGroupDef[] = [
  {
    items: [
      { to: "/", icon: "dashboard", label: "nav.dashboard", end: true },
      { to: "/trips", icon: "trips", label: "nav.trips" },
      { to: "/request", icon: "plus", label: "nav.request" },
      { to: "/calendar", icon: "calendar", label: "nav.calendar" },
    ],
  },
  {
    label: "nav.groupMyClub",
    items: [
      { to: "/roster", icon: "roster", label: "nav.roster" },
      { to: "/messages", icon: "messages", label: "nav.messages" },
      { to: "/invoices", icon: "invoices", label: "nav.invoices" },
    ],
  },
  {
    items: [{ to: "/settings", icon: "settings", label: "nav.settings" }],
  },
];

export function ClubLayout() {
  const { t } = useTranslation();
  const { me } = useAuthz();
  const navigate = useNavigate();

  const name = me?.name ?? "—";
  // Club roles are hidden in the club portal — show the club name as the account sub-label instead.
  const accountSub = me?.clubName ?? t("shell.clubPortal");
  const { density } = useDensity();

  return (
    <div className={cn("app", density === "compact" && "dense")}>
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <LogoIcon style={{ height: 30 }} />
          </div>
          <div>
            <div className="brand-name">Travel Connection</div>
            <div className="brand-sub">{t("shell.clubPortal")}</div>
          </div>
        </div>

        <SidebarNav groups={NAV} />

        <div className="sidebar-foot">
          <button type="button" className="club-card" onClick={() => navigate("/settings")}>
            <Avatar initials={initials(name)} src={me?.image} size={38} />
            <div className="meta">
              <b>{name}</b>
              <span>{accountSub}</span>
            </div>
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="topbar-spacer" />
          <Button size="sm" icon="plus" onClick={() => navigate("/request")}>
            {t("shell.newRequest")}
          </Button>
          <LangToggle />
          <NotificationsBell />
          <UserMenu name={name} role={accountSub} image={me?.image} />
        </header>

        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
